/**
 * GPT Banner Management Service
 * 
 * This service provides functionality for processing natural language commands
 * related to banner management and transforming them into API calls.
 */

// Mock implementation of the GPT processing
// In a production environment, this would call an actual GPT API
export default class BannerGptService {
  /**
   * Process a natural language command related to banner management
   * 
   * @param {string} prompt - The user's natural language command
   * @param {Array} currentBanners - The current list of banners for context
   * @returns {Object} The processed command result with action and data
   */
  static async processCommand(prompt, currentBanners) {
    // In a real implementation, this would call a GPT service
    // For now, we'll implement a basic parsing logic
    
    prompt = prompt.toLowerCase().trim();
    
    try {
      // Handle different types of commands
      if (this.isCreateCommand(prompt)) {
        return this.handleCreateCommand(prompt);
      } else if (this.isUpdateCommand(prompt)) {
        return this.handleUpdateCommand(prompt, currentBanners);
      } else if (this.isDeleteCommand(prompt)) {
        return this.handleDeleteCommand(prompt, currentBanners);
      } else if (this.isToggleActiveCommand(prompt)) {
        return this.handleToggleActiveCommand(prompt, currentBanners);
      } else if (this.isListCommand(prompt)) {
        return this.handleListCommand(prompt, currentBanners);
      } else {
        return {
          action: null,
          message: "I'm sorry, I couldn't understand that command. Please try one of the example commands or rephrase your request."
        };
      }
    } catch (error) {
      console.error('Error processing GPT command:', error);
      return {
        action: null,
        message: `Error processing your request: ${error.message}`
      };
    }
  }
  
  // Command type detection methods
  static isCreateCommand(prompt) {
    return prompt.includes('create') || prompt.includes('add') || prompt.includes('new');
  }
  
  static isUpdateCommand(prompt) {
    return prompt.includes('update') || prompt.includes('edit') || prompt.includes('change') || prompt.includes('modify');
  }
  
  static isDeleteCommand(prompt) {
    return prompt.includes('delete') || prompt.includes('remove');
  }
  
  static isToggleActiveCommand(prompt) {
    return (prompt.includes('active') || prompt.includes('inactive') || prompt.includes('enable') || 
            prompt.includes('disable') || prompt.includes('activate') || prompt.includes('deactivate')) && 
            !this.isCreateCommand(prompt);
  }
  
  static isListCommand(prompt) {
    return prompt.includes('list') || prompt.includes('show') || prompt.includes('display') || 
           prompt.includes('get') || prompt.includes('all') || prompt.includes('find');
  }
  
  // Command handlers
  static handleCreateCommand(prompt) {
    // Extract banner details from the prompt
    const title = this.extractValue(prompt, 'title', 'New Banner');
    const description = this.extractValue(prompt, 'description', 'Banner description');
    
    // Prepare banner data
    const bannerData = {
      title,
      title_ar: title, // You may want to handle translation differently
      description,
      description_ar: description, // You may want to handle translation differently
      image_url: 'https://placekitten.com/800/400', // Placeholder image URL
      image_url_ar: 'https://placekitten.com/800/400', // Placeholder image URL
      order: 1,
      course_id: null,
      expiry_date: null,
      is_active: prompt.includes('active') || !prompt.includes('inactive')
    };
    
    return {
      action: 'create',
      bannerData,
      message: `I've created a new banner with title "${title}". You'll need to update the banner images later.`
    };
  }
  
  static handleUpdateCommand(prompt, currentBanners) {
    // Extract banner ID from prompt
    const idMatch = prompt.match(/banner (?:with )?(?:id|number|#)?[:\s]*(\d+)/i);
    let bannerId = idMatch ? parseInt(idMatch[1]) : null;
    
    // If no ID is found, try to find by title
    if (!bannerId) {
      const titleMatch = prompt.match(/banner ["'](.+?)["']/i);
      if (titleMatch) {
        const title = titleMatch[1];
        const banner = currentBanners.find(b => 
          b.title.toLowerCase().includes(title.toLowerCase())
        );
        if (banner) {
          bannerId = banner.id;
        }
      }
    }
    
    if (!bannerId) {
      return {
        action: null,
        message: "I couldn't identify which banner you want to update. Please specify the banner ID or title."
      };
    }
    
    // Find the banner to update
    const bannerToUpdate = currentBanners.find(b => b.id === bannerId);
    if (!bannerToUpdate) {
      return {
        action: null,
        message: `I couldn't find a banner with ID ${bannerId}. Please check the ID and try again.`
      };
    }
    
    // Create a copy of the banner to update
    const bannerData = { ...bannerToUpdate };
    
    // Update fields based on prompt
    if (prompt.includes('title')) {
      const title = this.extractValue(prompt, 'title', bannerData.title);
      bannerData.title = title;
    }
    
    if (prompt.includes('description')) {
      const description = this.extractValue(prompt, 'description', bannerData.description);
      bannerData.description = description;
    }
    
    if (prompt.includes('order')) {
      const orderMatch = prompt.match(/order(?:[:=\s]+)(\d+)/i);
      if (orderMatch) {
        bannerData.order = parseInt(orderMatch[1]);
      }
    }
    
    if (prompt.includes('active') || prompt.includes('inactive')) {
      bannerData.is_active = prompt.includes('active') && !prompt.includes('inactive');
    }
    
    return {
      action: 'update',
      bannerId,
      bannerData,
      message: `I've updated the banner with ID ${bannerId}.`
    };
  }
  
  static handleDeleteCommand(prompt, currentBanners) {
    // Extract banner ID from prompt
    const idMatch = prompt.match(/banner (?:with )?(?:id|number|#)?[:\s]*(\d+)/i);
    let bannerId = idMatch ? parseInt(idMatch[1]) : null;
    
    // If no ID is found, try to find by title
    if (!bannerId) {
      const titleMatch = prompt.match(/banner ["'](.+?)["']/i);
      if (titleMatch) {
        const title = titleMatch[1];
        const banner = currentBanners.find(b => 
          b.title.toLowerCase().includes(title.toLowerCase())
        );
        if (banner) {
          bannerId = banner.id;
        }
      }
    }
    
    if (!bannerId) {
      return {
        action: null,
        message: "I couldn't identify which banner you want to delete. Please specify the banner ID or title."
      };
    }
    
    // Find the banner to delete
    const bannerToDelete = currentBanners.find(b => b.id === bannerId);
    if (!bannerToDelete) {
      return {
        action: null,
        message: `I couldn't find a banner with ID ${bannerId}. Please check the ID and try again.`
      };
    }
    
    return {
      action: 'delete',
      bannerId,
      message: `I've deleted the banner with ID ${bannerId} (${bannerToDelete.title}).`
    };
  }
  
  static handleToggleActiveCommand(prompt, currentBanners) {
    // Extract banner ID from prompt
    const idMatch = prompt.match(/banner (?:with )?(?:id|number|#)?[:\s]*(\d+)/i);
    let bannerId = idMatch ? parseInt(idMatch[1]) : null;
    
    // If no ID is found, try to find by title
    if (!bannerId) {
      const titleMatch = prompt.match(/banner ["'](.+?)["']/i);
      if (titleMatch) {
        const title = titleMatch[1];
        const banner = currentBanners.find(b => 
          b.title.toLowerCase().includes(title.toLowerCase())
        );
        if (banner) {
          bannerId = banner.id;
        }
      }
    }
    
    if (!bannerId) {
      return {
        action: null,
        message: "I couldn't identify which banner you want to toggle. Please specify the banner ID or title."
      };
    }
    
    // Find the banner to toggle
    const bannerToToggle = currentBanners.find(b => b.id === bannerId);
    if (!bannerToToggle) {
      return {
        action: null,
        message: `I couldn't find a banner with ID ${bannerId}. Please check the ID and try again.`
      };
    }
    
    const makeActive = prompt.includes('active') || prompt.includes('enable') || prompt.includes('activate');
    const makeInactive = prompt.includes('inactive') || prompt.includes('disable') || prompt.includes('deactivate');
    
    // If both are mentioned or neither, toggle the current state
    const newState = makeActive && !makeInactive ? true : 
                    makeInactive && !makeActive ? false : 
                    !bannerToToggle.is_active;
    
    return {
      action: 'toggle',
      bannerId,
      makeActive: newState,
      message: `I've ${newState ? 'activated' : 'deactivated'} the banner with ID ${bannerId} (${bannerToToggle.title}).`
    };
  }
  
  static handleListCommand(prompt, currentBanners) {
    // Filter banners based on prompt
    let filteredBanners = [...currentBanners];
    
    if (prompt.includes('active') && !prompt.includes('inactive')) {
      filteredBanners = filteredBanners.filter(b => b.is_active);
    } else if (prompt.includes('inactive') && !prompt.includes('active')) {
      filteredBanners = filteredBanners.filter(b => !b.is_active);
    }
    
    // Sort by order
    filteredBanners.sort((a, b) => a.order - b.order);
    
    // Create a readable summary
    let message = `Here are the ${filteredBanners.length} banners`;
    if (prompt.includes('active') && !prompt.includes('inactive')) {
      message += ' that are currently active';
    } else if (prompt.includes('inactive') && !prompt.includes('active')) {
      message += ' that are currently inactive';
    }
    message += ':<br/><br/>';
    
    filteredBanners.forEach(banner => {
      message += `• ID: ${banner.id} - "${banner.title}" - Order: ${banner.order} - ${banner.is_active ? 'Active' : 'Inactive'}<br/>`;
    });
    
    return {
      action: 'list',
      banners: filteredBanners,
      message
    };
  }
  
  // Helper methods
  static extractValue(prompt, key, defaultValue) {
    // First try with quotes
    const quotedMatch = prompt.match(new RegExp(`${key}\\s*[:"']\\s*([^"']+)["']`, 'i'));
    if (quotedMatch) {
      return quotedMatch[1].trim();
    }
    
    // Try with key:value format
    const keyValueMatch = prompt.match(new RegExp(`${key}\\s*[:=]\\s*([\\w\\s]+?)(?:\\s+and\\s+|\\s+with\\s+|\\s+set\\s+|$)`, 'i'));
    if (keyValueMatch) {
      return keyValueMatch[1].trim();
    }
    
    return defaultValue;
  }
} 